import React, { useState, useEffect, useCallback } from 'react';
import { getReportsUserSummary, getActiveUsersTimeSeries } from '../../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function ReportsOverview() {
  const [summaryData, setSummaryData] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState(null);
  const [chartDays, setChartDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getReportsUserSummary();
      setSummaryData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch overview data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTimeSeries = useCallback(async () => {
    try {
      const result = await getActiveUsersTimeSeries(chartDays);
      setTimeSeriesData(result);
    } catch (err) {
      console.error('Failed to fetch time series data:', err);
    }
  }, [chartDays]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchTimeSeries();
  }, [fetchTimeSeries]);

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading analytics overview...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-error">
        <p>{error}</p>
        <button className="refresh-btn" onClick={fetchSummary}>Retry</button>
      </div>
    );
  }

  const periods = summaryData?.periods || {};
  const p24h = periods['24h'] || {};
  const p7d = periods['7d'] || {};
  const p30d = periods['30d'] || {};
  const p90d = periods['90d'] || {};

  const activityRate = p30d.unique_users
    ? (p30d.total_activities / p30d.unique_users).toFixed(1)
    : '0.0';

  const chartData = (timeSeriesData?.time_series || []).map((item) => ({
    ...item,
    dateLabel: formatDate(item.date),
  }));

  return (
    <div className="reports-overview">
      {/* Top KPI Cards */}
      <div className="analytics-kpi-cards">
        <div className="analytics-kpi-card" style={{ borderLeftColor: '#218aff' }}>
          <div className="kpi-label">DAU (Today)</div>
          <div className="kpi-value" style={{ color: '#218aff' }}>
            {p24h.unique_users || 0}
          </div>
          <div className="kpi-sublabel">Daily Active Users</div>
        </div>

        <div className="analytics-kpi-card" style={{ borderLeftColor: '#10b981' }}>
          <div className="kpi-label">WAU (7 Days)</div>
          <div className="kpi-value" style={{ color: '#10b981' }}>
            {p7d.unique_users || 0}
          </div>
          <div className="kpi-sublabel">Weekly Active Users</div>
        </div>

        <div className="analytics-kpi-card" style={{ borderLeftColor: '#8b5cf6' }}>
          <div className="kpi-label">MAU (30 Days)</div>
          <div className="kpi-value" style={{ color: '#8b5cf6' }}>
            {p30d.unique_users || 0}
          </div>
          <div className="kpi-sublabel">Monthly Active Users</div>
        </div>

        <div className="analytics-kpi-card" style={{ borderLeftColor: '#f59e0b' }}>
          <div className="kpi-label">Total Events (30d)</div>
          <div className="kpi-value" style={{ color: '#f59e0b' }}>
            {p30d.total_activities || 0}
          </div>
          <div className="kpi-sublabel">Activities in Last 30 Days</div>
        </div>
      </div>

      {/* DAU / WAU / MAU Line Chart */}
      <div className="analytics-chart-container">
        <div className="analytics-chart-header">
          <h3>Active Users Over Time</h3>
          <div className="period-selector">
            <label htmlFor="chart-days-select">Period:</label>
            <select
              id="chart-days-select"
              value={chartDays}
              onChange={(e) => setChartDays(Number(e.target.value))}
              className="period-select"
            >
              <option value={30}>Last 30 Days</option>
              <option value={60}>Last 60 Days</option>
              <option value={90}>Last 90 Days</option>
            </select>
          </div>
        </div>

        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis
                dataKey="dateLabel"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#333' }}
                tickLine={{ stroke: '#333' }}
              />
              <YAxis
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#333' }}
                tickLine={{ stroke: '#333' }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#fff',
                }}
                labelStyle={{ color: '#aeb9cc' }}
                itemStyle={{ color: '#fff' }}
              />
              <Legend
                wrapperStyle={{ color: '#fff', paddingTop: '10px' }}
              />
              <Line
                type="monotone"
                dataKey="dau"
                name="DAU"
                stroke="#218aff"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: '#218aff' }}
              />
              <Line
                type="monotone"
                dataKey="wau"
                name="WAU"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: '#10b981' }}
              />
              <Line
                type="monotone"
                dataKey="mau"
                name="MAU"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: '#8b5cf6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="no-data" style={{ padding: '40px', textAlign: 'center' }}>
            No time series data available for the selected period.
          </div>
        )}
      </div>

      {/* Quick Stats Row */}
      <div className="analytics-quick-stats">
        <div className="analytics-quick-stat-card">
          <div className="quick-stat-label">Total Signups (90d)</div>
          <div className="quick-stat-value">{p90d.unique_users || 0}</div>
        </div>

        <div className="analytics-quick-stat-card">
          <div className="quick-stat-label">Logins Today</div>
          <div className="quick-stat-value">{p24h.logins || 0}</div>
        </div>

        <div className="analytics-quick-stat-card">
          <div className="quick-stat-label">Activity Rate (30d)</div>
          <div className="quick-stat-value">{activityRate}</div>
          <div className="quick-stat-sublabel">events per user</div>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length < 3) return dateStr;
  return `${parts[1]}/${parts[2]}`;
}

export default ReportsOverview;
